import React from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, XCircle, Droplets, Package, Trash2, AlertTriangle } from 'lucide-react';

const HowToSortPage = () => {
  const sortingGuides = [
    {
      name: 'Stiklo Konteineris',
      color: 'hsl(150 50% 45%)',
      icon: Droplets,
      canSort: [
        'Stikliniai buteliai',
        'Maisto produktų stiklinė tara',
        'Stiklainiai be dangtelio',
        'Aliejaus stikliniai buteliai',
        'Kosmetikos priemonių stiklinė tara',
        'Kvepalų stikliniai buteliukai',
        'Stiklo pakuočių duženos ir šukės',
      ],
      cannotSort: [
        'Veidrodžiai',
        'Porcelianas',
        'Krištolas',
        'Keramika',
        'Visos elektros lemputės',
        'TV ekranai',
        'Namų langų stiklai',
        'Automobilių stiklai',
      ],
      tips: [
        'Pakuočių plauti nebūtina, tik išpilti skysčius',
        'Etiketės galima palikti',
      ],
    },
    {
      name: 'Popieriaus Konteineris',
      color: 'hsl(210 60% 50%)',
      icon: Package,
      canSort: [
        'Popieriniai pirkinių maišeliai',
        'Visos rūšių popieriniai ir kartoniniai',
        'Popieriniai ir kartoniniai maisto pakuotės',
        'Popieriniai pakavimo priemonės',
        'Popieriniai pakuočių užpildai',
        'Kitos popieriaus ir kartono pakuočių atliekos',
      ],
      cannotSort: [
        'Sauskelnės',
        'Drėgnos ir vienkartines servetėlės',
        'Tualetinis popierius',
        'Popierinis rankšluostis',
        'Popieriniai pakuotės su maisto likučiais',
        'Tapetai',
        'Suteptas popierius (tepalais, dažais)',
      ],
      tips: [
        'Popierius turi būti sausas',
        'Sąvaržėlių išsegti nereikia',
        'Dėžutes išlankstykite',
      ],
    },
    {
      name: 'Plastiko ir Metalo Konteineris',
      color: 'hsl(45 85% 55%)',
      icon: Trash2,
      canSort: [
        'Visos maisto pakuotės',
        'Gėrimų buteliai',
        'Aliejaus buteliai',
        'Kosmetikos ir higienos produktų pakuotės',
        'Namų švaros produktų pakuotės',
        'Maisto išsinešti pakuotės',
        'Skardinės',
        'Metaliniai dangteliai',
      ],
      cannotSort: [
        'Tepalo bakeliai',
        'Indeliai nuo dažų',
        'Aušinimo skysčio pakuotės',
        'Smulki ir stambi elektroninė įranga',
        'Dantų šepetėliai, skutimosi peiliukai',
        'Bet kokie plastikiniai daiktai',
        'Įvairūs žaislai',
      ],
      tips: [
        'Pašalinkite maisto likučius bei išpilkite skysčius',
        'Pakuočių plauti nereikia',
        'Butelius užsukite',
      ],
    },
  ];

  const generalTips = [
    {
      title: 'Rūšiavimo konteineriai',
      content: 'Rūšiavimo konteineriai yra skirti produktų pakuočių atliekoms surinkti. Pirmiausia įvertinkite, ar tai, ką ketinate išmesti, yra pakuotė, ar pats produktas.',
    },
    {
      title: 'Pakuočių švarinimas',
      content: 'Iš išmetamų pakuočių rekomenduojama pašalinti jose likusius skysčių ar maisto likučius, kad nebūtų suteptos kitos pakuotės. Plauti pakuočių nebūtina.',
    },
    {
      title: 'Pakuočių suspaudimas',
      content: 'Jei galime, išmetamas pakuotes išlankstykime, suspauskime – dėl to jos užims mažiau vietos konteineryje.',
    },
    {
      title: 'Dydis turi reikšmę',
      content: 'Jei išmetamos pakuotės nelenda pro konteinerio angą, pakuotes reiktų susmulkinti arba priduoti stambiųjų atliekų surinkimo aikštelėse.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Header */}
      <section className="pt-24 pb-12 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
              Praktiniai Patarimai
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              Kaip Rūšiuoti Atliekas
            </h1>
            <p className="text-lg text-muted-foreground">
              Viską, ką reikia žinoti apie teisingą atliekų rūšiavimą
            </p>
          </div>
        </div>
      </section>

      {/* General Tips */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              Bendros Rūšiavimo Taisyklės
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {generalTips.map((tip, index) => (
                <Card key={index} className="hover-lift">
                  <CardHeader>
                    <CardTitle className="text-lg">{tip.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {tip.content}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sorting Guides */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              Rūšiavimo Gairės Pagal Konteinerius
            </h2>
            <div className="space-y-8">
              {sortingGuides.map((guide, index) => {
                const Icon = guide.icon;
                return (
                  <Card key={index} className="overflow-hidden">
                    <div className="h-2" style={{ backgroundColor: guide.color }}></div>
                    <CardHeader>
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: guide.color }}
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <CardTitle className="text-2xl">{guide.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Can Sort */}
                        <div>
                          <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2">
                            <CheckCircle2 className="w-5 h-5 text-primary" />
                            <span>Ką galima mesti</span>
                          </h3>
                          <ul className="space-y-2">
                            {guide.canSort.map((item, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                                <span className="text-sm text-muted-foreground">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Cannot Sort */}
                        <div>
                          <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2">
                            <XCircle className="w-5 h-5 text-destructive" />
                            <span>Ką negalima mesti</span>
                          </h3>
                          <ul className="space-y-2">
                            {guide.cannotSort.map((item, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <XCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-1" />
                                <span className="text-sm text-muted-foreground">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Tips */}
                      <div className="bg-muted rounded-lg p-4">
                        <h3 className="font-semibold mb-3 flex items-center space-x-2">
                          <AlertTriangle className="w-5 h-5 text-accent" />
                          <span>Patarimai</span>
                        </h3>
                        <ul className="space-y-2">
                          {guide.tips.map((tip, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start space-x-2">
                              <span className="text-accent flex-shrink-0">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Important Info */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  Svarbi Informacija
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Elektroninės atliekos:</strong> Elektroninių atliekų surinkimo punktus rasite{' '}
                  <a href="https://www.epa.lt/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    čia
                  </a>.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Stambiųjų atliekų aikštelės:</strong> Stambiųjų, arba didžiagaritų, atliekų aikštelių sąrašą rasite{' '}
                  <a href="https://aaa.lrv.lt/lt/veiklos-sritys/atliekos/atlieku-tvarkytojai/dideliu-gabaritu-stambiagabariciu-atlieku-surinkimo-aiksteles-lietuvoje" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    čia
                  </a>.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Pavojingos atliekos:</strong> Kai kurios atliekos yra pavojingos ir negali būti nei rūšiuojamos, nei išmetamos į buitinių atliekų konteinerių. Jas galima priduoti stambiagabaritių atliekų aikštelėse.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowToSortPage;